/**
 * conversation-model-versioning Phase 5 Session 1
 *
 * Client mirror of the ADR-0009 completeness gate
 * (`apps/api/src/modules/conversations/completeness-gate.service.ts`).
 *
 * PURPOSE: drive the "Create version" / "Accept" disabled-tooltip in `<ManageVersionsButton>`.
 * This is DISPLAY-ONLY — the server enforces the real gate at POST /versions (P2).
 *
 * Critical predicate rules (risk #50):
 *  - An input is addressed when `source !== 'default'` OR
 *    `defaultStatus ∈ {accepted, confirmed, overridden}`.
 *    `'applied'` is a SPEC PHANTOM — it does NOT exist in `defaultStatusSchema`
 *    and is NOT a valid addressed state.
 *  - Location branch (ADR 0003): if `inputDataTypes[key] === 'location'`, the input
 *    is addressed ONLY when `source === 'user'` AND a resolved `dealMetadata`
 *    payload exists for that field. Market/Arcanum-default does NOT count.
 *  - Denominator = `schemaInputs.length`. NEVER `Object.keys(inputValues).length`.
 *    NEVER `|| 43` (no hardcoded fallback).
 *  - `orphanedInputs` non-empty → not ready (mirrors P2 `computeCompleteness`).
 */

import type { InputDefinition } from '@arcanum-hq/shared-types';

/** Subset of ProvenanceRecord fields needed for the completeness check. */
export interface ProvenanceEntry {
  source: string;
  defaultStatus?: string | null;
}

/** Resolved deal-metadata (creator-intent). Only the location-relevant field. */
export interface DealMetadataLike {
  market?: string | null;
  [key: string]: unknown;
}

/** Per-input data-type lookup keyed by `InputDefinition.name`. */
export type InputDataTypes = Record<string, string>;

/** Display names by input name — `displayName ?? name` fallback at call sites. */
export type InputDisplayNames = Record<string, string>;

/** One unaddressed input surfaced in the tooltip. */
export interface UnaddressedInput {
  /** Human-readable label for the tooltip (never an internal named-range key). */
  displayName: string;
  /** True when the input has `dataType === 'location'` — renders distinct copy. */
  isLocation: boolean;
}

/** Return shape of `computeVersionReadiness`. */
export interface VersionReadiness {
  ready: boolean;
  unaddressed: UnaddressedInput[];
}

export const ADDRESSED_DEFAULT_STATUSES = new Set([
  'accepted',
  'confirmed',
  'overridden',
]);

/**
 * Standard addressed-input predicate (ADR-0009):
 *   addressed = source !== 'default' OR defaultStatus ∈ {accepted,confirmed,overridden}
 *
 * Exported so `VariablesPanel` and `builder.tsx` counter can import the same
 * predicate rather than re-deriving it inline (Phase 3 hardening, 2026-05-18).
 */
export function isAddressedStandard(entry: ProvenanceEntry | null): boolean {
  if (entry == null) return false;
  const { source } = entry;
  if (source != null && source !== 'default') return true;
  return ADDRESSED_DEFAULT_STATUSES.has(entry.defaultStatus ?? '');
}

/**
 * Location addressed predicate — STRICT mirror of the api-side
 * `completeness-gate.service.ts:isAddressedLocation` (ADR-0003 +
 * variables-counter-coherence Inc-E, 2026-05-20).
 *
 * A location input is addressed ONLY when:
 *   - source === 'user' (explicit user-provided address via
 *     `set_property_address` — the only valid way to set a location), AND
 *   - dealMetadata[key] is non-null (the `PropertyResolution` payload
 *     written by the api-side `applyResolutionToAgentState` compose helper).
 *
 * ## Inc-E (2026-05-20) — STRICT mirror of the api-side predicate
 *
 * Prior to Inc-E this predicate had a SECOND branch accepting
 * `source === 'default' && defaultStatus ∈ {accepted, confirmed, overridden}`.
 * That branch was a "Phase 3 hardening" fix from before the variables-counter-
 * coherence Inc-B contract landed (which makes location inputs NEVER seeded as
 * defaults at conversation create time). Under Inc-B + Inc-D, the relaxed
 * branch is unreachable on new conversations AND silently masks the regression
 * if a legacy seed loop ever re-introduces default-sourced location entries —
 * the web counter would read "addressed" while the api gate (which has always
 * required strict source==='user') reads "not addressed". That divergence is
 * exactly the kind of cross-domain drift the Inc-E defensive layer exists to
 * lock out.
 *
 * The behavior change for legacy conversations: a stale `source: 'default' +
 * defaultStatus: 'accepted'` location entry from a pre-Inc-B conversation now
 * reads as "not set" on the web AND is correctly reported as unaddressed by
 * the server. Users on those conversations re-resolve via `set_property_address`
 * (the agent's `<address_capture>` block handles the prompt automatically).
 *
 * NOTE: source === 'market_data' continues to NOT satisfy this predicate.
 * Market data ENRICHES a location but never SETS one. Do NOT replace this
 * with `isAddressedStandard(entry) && hasDealMetadataForKey` — that would
 * incorrectly mark market_data-sourced locations as resolved.
 */
export function isAddressedLocation(
  entry: ProvenanceEntry | null,
  hasDealMetadataForKey: boolean
): boolean {
  if (entry == null) return false;
  if (entry.source !== 'user') return false;
  return hasDealMetadataForKey;
}

/**
 * Returns whether `schemaInputs` are sufficiently addressed to allow
 * creating a model version, plus the list of inputs blocking readiness.
 *
 * @param schemaInputs      `modelDetail?.data?.inputs` — the authoritative list
 *                          from the schema (denominator = `.length`).
 * @param inputProvenance   `agent_state.inputProvenance` from the active
 *                          conversation — keyed by named-range name.
 * @param inputDataTypes    Named-range name → DataType string (e.g. 'location').
 * @param dealMetadata      `agent_state.dealMetadata` — used to check whether a
 *                          location input has a resolved address payload.
 * @param orphanedInputs    `agent_state.orphanedInputs` — non-empty means the
 *                          model schema changed and unresolved drift exists.
 */
export function computeVersionReadiness(
  schemaInputs: (Pick<InputDefinition, 'name' | 'displayName'> & {
    dataType?: InputDefinition['dataType'];
  })[],
  inputProvenance: Record<string, ProvenanceEntry | string | undefined>,
  inputDataTypes: InputDataTypes,
  dealMetadata: DealMetadataLike | null | undefined,
  orphanedInputs: Record<string, unknown>
): VersionReadiness {
  // Orphaned inputs (schema drift) block version creation — mirrors P2 gate.
  if (Object.keys(orphanedInputs).length > 0) {
    // Surface all schema inputs as unaddressed when orphans exist (we don't
    // know which are still valid). This mirrors the server's early-return.
    const unaddressed = schemaInputs.map((inp) => ({
      displayName: inp.displayName ?? inp.name,
      isLocation: (inputDataTypes[inp.name] ?? inp.dataType) === 'location',
    }));
    return { ready: false, unaddressed };
  }

  const unaddressed: UnaddressedInput[] = [];

  for (const inp of schemaInputs) {
    const key = inp.name;
    const dataType = inputDataTypes[key] ?? inp.dataType;
    const isLocation = dataType === 'location';

    const prov = inputProvenance[key];
    const entry: ProvenanceEntry | null =
      prov == null ? null : typeof prov === 'string' ? { source: prov } : prov;

    const source = entry?.source;

    if (isLocation) {
      // ADR 0003: location input addressed ONLY when source === 'user' AND
      // a resolved dealMetadata payload exists for that field.
      // `market` is the canonical location field projected from dealMetadata.
      const locationAddressed =
        source === 'user' &&
        dealMetadata != null &&
        typeof dealMetadata.market === 'string' &&
        dealMetadata.market.trim().length > 0;

      if (!locationAddressed) {
        unaddressed.push({
          displayName: inp.displayName ?? key,
          isLocation: true,
        });
      }
      continue;
    }

    // Standard branch (ADR 0009):
    // addressed = source !== 'default' OR defaultStatus ∈ {accepted,confirmed,overridden}
    const isAddressed =
      source != null && source !== 'default'
        ? true
        : ADDRESSED_DEFAULT_STATUSES.has(entry?.defaultStatus ?? '');

    if (!isAddressed) {
      unaddressed.push({
        displayName: inp.displayName ?? key,
        isLocation: false,
      });
    }
  }

  return { ready: unaddressed.length === 0, unaddressed };
}
